/*
 * @bot-written
 *
 * WARNING AND NOTICE
 * Any access, download, storage, and/or use of this source code is subject to the terms and conditions of the
 * Full Software Licence as accepted by you before being granted access to this source code and other materials,
 * the terms of which can be accessed on the Codebots website at https://codebots.com/full-software-licence. Any
 * commercial use in contravention of the terms of the Full Software Licence may be pursued by Codebots through
 * licence termination and further legal action, and be required to indemnify Codebots for any loss or damage,
 * including interest and costs. You are deemed to have accepted the terms of the Full Software Licence on any
 * access, download, storage, and/or use of this source code.
 *
 * BOT WARNING
 * This file is bot-written.
 * Any changes out side of "protected regions" will be lost next time the bot makes any changes.
 */
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import { IOrderByCondition } from 'Views/Components/ModelCollection/ModelQuery';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import AgencyPlaylisterCountryEntity from 'Models/Security/Acl/AgencyPlaylisterCountryEntity';
import ApplicationUserCountryEntity from 'Models/Security/Acl/ApplicationUserCountryEntity';
import AgencyAdminCountryEntity from 'Models/Security/Acl/AgencyAdminCountryEntity';
import VisitorsCountryEntity from 'Models/Security/Acl/VisitorsCountryEntity';
import MustardManagementCountryEntity from 'Models/Security/Acl/MustardManagementCountryEntity';
import MustardAdminCountryEntity from 'Models/Security/Acl/MustardAdminCountryEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
// % protected region % [Add any further imports here] off begin
// % protected region % [Add any further imports here] end

export interface ICountryEntityAttributes extends IModelAttributes {
	name: string;

	regionId?: string;
	region?: Models.RegionEntity | Models.IRegionEntityAttributes;
	venuess: Array<
		| Models.VenueEntity
		| Models.IVenueEntityAttributes
	>;
	// % protected region % [Add any custom attributes to the interface here] off begin
	// % protected region % [Add any custom attributes to the interface here] end
}

// % protected region % [Customise your entity metadata here] off begin
@entity('CountryEntity', 'Country')
// % protected region % [Customise your entity metadata here] end
export default class CountryEntity extends Model
	implements ICountryEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new AgencyPlaylisterCountryEntity(),
		new ApplicationUserCountryEntity(),
		new AgencyAdminCountryEntity(),
		new VisitorsCountryEntity(),
		new MustardManagementCountryEntity(),
		new MustardAdminCountryEntity(),
		// % protected region % [Add any further ACL entries here] off begin
		// % protected region % [Add any further ACL entries here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [
		// % protected region % [Add any custom create exclusions here] off begin
		// % protected region % [Add any custom create exclusions here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		// % protected region % [Add any custom update exclusions here] off begin
		// % protected region % [Add any custom update exclusions here] end
	];

	/**
	 * The default order by field when the collection is loaded .
	 */
	public get orderByField(): IOrderByCondition<Model> | undefined {
		// % protected region % [Modify the order by field here] off begin
		return {
			path: 'name',
			descending: false,
		};
		// % protected region % [Modify the order by field here] end
	}

	// % protected region % [Modify props to the crud options here for attribute 'Name'] off begin
	@Validators.Required()
	@observable
	@attribute<CountryEntity, string>()
	@CRUD({
		name: 'Name',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public name: string;
	// % protected region % [Modify props to the crud options here for attribute 'Name'] end

	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Region'] off begin
		name: 'Region',
		displayType: 'reference-combobox',
		order: 20,
		referenceTypeFunc: () => Models.RegionEntity,
		// % protected region % [Modify props to the crud options here for reference 'Region'] end
	})
	public regionId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public region: Models.RegionEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Venues'] off begin
		name: 'Venuess',
		displayType: 'reference-multicombobox',
		order: 30,
		referenceTypeFunc: () => Models.VenueEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'venuess',
			oppositeEntity: () => Models.VenueEntity,
		}),
		// % protected region % [Modify props to the crud options here for reference 'Venues'] end
	})
	public venuess: Models.VenueEntity[] = [];

	// % protected region % [Add any custom attributes to the model here] off begin
	// % protected region % [Add any custom attributes to the model here] end

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<ICountryEntityAttributes>) {
		// % protected region % [Add any extra constructor logic before calling super here] off begin
		// % protected region % [Add any extra constructor logic before calling super here] end

		super(attributes);

		// % protected region % [Add any extra constructor logic after calling super here] off begin
		// % protected region % [Add any extra constructor logic after calling super here] end
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<ICountryEntityAttributes>) {
		// % protected region % [Override assign attributes here] off begin
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.regionId !== undefined) {
				this.regionId = attributes.regionId;
			}
			if (attributes.region !== undefined) {
				if (attributes.region === null) {
					this.region = attributes.region;
				} else if (attributes.region instanceof Models.RegionEntity) {
					this.region = attributes.region;
					this.regionId = attributes.region.id;
				} else {
					this.region = new Models.RegionEntity(attributes.region);
					this.regionId = this.region.id;
				}
			}
			if (attributes.venuess !== undefined && Array.isArray(attributes.venuess)) {
				for (const model of attributes.venuess) {
					if (model instanceof Models.VenueEntity) {
						this.venuess.push(model);
					} else {
						this.venuess.push(new Models.VenueEntity(model));
					}
				}
			}
			// % protected region % [Override assign attributes here] end

			// % protected region % [Add any extra assign attributes logic here] off begin
			// % protected region % [Add any extra assign attributes logic here] end
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	// % protected region % [Customize Default Expands here] off begin
	public defaultExpands = `
		region {
			${Models.RegionEntity.getAttributes().join('\n')}
		}
		venuess {
			${Models.VenueEntity.getAttributes().join('\n')}
			${Models.VenueEntity.getFiles().map(f => f.name).join('\n')}
		}
	`;
	// % protected region % [Customize Default Expands here] end

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// % protected region % [Customize Save From Crud here] off begin
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			venuess: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'venuess',
						],
					},
				],
			},
		);
	}
	// % protected region % [Customize Save From Crud here] end

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] on begin
		return this.name;
		// % protected region % [Customise the display name for this entity] end
	}

	// % protected region % [Add any further custom model features here] off begin
	// % protected region % [Add any further custom model features here] end
}

// % protected region % [Modify the create and modified CRUD attributes here] off begin
/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(CountryEntity.prototype, 'created');
CRUD(modifiedAttr)(CountryEntity.prototype, 'modified');
// % protected region % [Modify the create and modified CRUD attributes here] end
